import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2 } from "lucide-react";
import { TrustpilotBadge } from "./TrustpilotBadge";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import axios from "axios";
import {
  SYMPTOMS,
  STAGE_OPTIONS,
  HRT_OPTIONS,
} from "../mock";
import { track } from "../lib/analytics";
import { getAttribution } from "../lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOAD_TEXT = ["Reading your symptoms…", "Mapping your systems…", "Building your Matrix…"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const emptyState = {
  checkedItems: [],
  primary_symptom: "",
  stage: "",
  hrt_status: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  instagram_handle: "",
  consent_marketing: false,
};

const Matrix = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(emptyState);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadIdx, setLoadIdx] = useState(0);
  const startedRef = useRef(false);

  // Restore from sessionStorage
  useEffect(() => {
    try {
      const s = sessionStorage.getItem("matrix_state");
      const st = sessionStorage.getItem("matrix_step");
      if (s) setData({ ...emptyState, ...JSON.parse(s) });
      if (st) setStep(Number(st));
    } catch (e) {}
  }, []);

  // Persist
  useEffect(() => {
    try {
      sessionStorage.setItem("matrix_state", JSON.stringify(data));
      sessionStorage.setItem("matrix_step", String(step));
    } catch (e) {}
  }, [data, step]);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const toggleItem = (id) => {
    if (!startedRef.current) {
      startedRef.current = true;
      track("matrix_start");
    }
    setError("");
    setData((d) => {
      const has = d.checkedItems.includes(id);
      const checkedItems = has
        ? d.checkedItems.filter((x) => x !== id)
        : [...d.checkedItems, id];
      // keep primary_symptom valid
      const primary_symptom = checkedItems.includes(d.primary_symptom)
        ? d.primary_symptom
        : "";
      return { ...d, checkedItems, primary_symptom };
    });
  };

  // Ordered list of checked items (Step 1 order) for the dropdown
  const checkedOrdered = useMemo(
    () => SYMPTOMS.filter((s) => data.checkedItems.includes(s.id)),
    [data.checkedItems]
  );

  // Auto-select & lock dropdown when exactly one checked
  useEffect(() => {
    if (step === 2 && checkedOrdered.length === 1 && data.primary_symptom !== checkedOrdered[0].id) {
      update({ primary_symptom: checkedOrdered[0].id });
    }
  }, [step, checkedOrdered]);

  const next = () => {
    setError("");
    if (step === 1) {
      if (data.checkedItems.length < 1) {
        setError("Check at least one to build your Matrix.");
        return;
      }
      track("matrix_step2");
      setStep(2);
    } else if (step === 2) {
      if (!data.primary_symptom || !data.stage || !data.hrt_status) {
        setError("Please answer all three questions.");
        return;
      }
      track("matrix_step3");
      setStep(3);
    }
    window.scrollTo({ top: document.getElementById("matrix").offsetTop - 70, behavior: "smooth" });
  };

  const back = () => {
    setError("");
    setStep((s) => Math.max(1, s - 1));
  };

  const submit = async () => {
    setError("");
    const name = data.first_name.trim();
    if (name.length < 1 || name.length > 40) {
      setError("Please enter your first name.");
      return;
    }
    const lastName = data.last_name.trim();
    if (lastName.length < 1 || lastName.length > 40) {
      setError("Please enter your last name.");
      return;
    }
    if (!EMAIL_RE.test(data.email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    const phone = data.phone.trim();
    if (phone && phone.replace(/\D/g, "").length < 10) {
      setError("Please enter a valid cell phone number, or leave it blank.");
      return;
    }
    if (!data.consent_marketing) {
      setError("Please check the box so we can send your results.");
      return;
    }

    setLoading(true);
    setLoadIdx(0);
    const t1 = setTimeout(() => setLoadIdx(1), 800);
    const t2 = setTimeout(() => setLoadIdx(2), 1600);

    try {
      const [resp] = await Promise.all([
        axios.post(`${API}/matrix`, {
          checked_items: data.checkedItems,
          primary_symptom: data.primary_symptom,
          stage: data.stage,
          hrt_status: data.hrt_status,
          first_name: name,
          last_name: lastName,
          email: data.email.trim(),
          phone: data.phone.trim(),
          instagram_handle: data.instagram_handle.trim(),
          consent_marketing: data.consent_marketing,
          attribution: getAttribution(),
          origin: window.location.origin,
        }),
        // Hold a minimum of 2.0s for the analysis feel
        new Promise((r) => setTimeout(r, 2000)),
      ]);
      clearTimeout(t1);
      clearTimeout(t2);
      track("matrix_complete", { form_type: "matrix" });
      track("generate_lead", { form_type: "matrix" }, "Lead");
      sessionStorage.removeItem("matrix_state");
      sessionStorage.removeItem("matrix_step");
      navigate(`/matrix/results/${resp.data.result_id}`);
    } catch (err) {
      clearTimeout(t1);
      clearTimeout(t2);
      setLoading(false);
      setError("Something went wrong building your Matrix. Please try again.");
    }
  };

  return (
    <section id="matrix" className="scroll-mt-20 pt-8 pb-12 md:pt-12 md:pb-20 relative">
      <div className="w-[90%] max-w-[840px] mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.95] text-primary text-4xl sm:text-5xl lg:text-6xl">
            The Menopause Matrix<sup className="align-super text-[0.5em]">™</sup>
          </h2>
          <p className="mt-4 font-display font-black uppercase tracking-tight leading-[1.05] text-white text-xl md:text-2xl">
            Find out which systems are driving what you feel.
          </p>
          <p className="mt-5 text-lg text-white/70 font-medium max-w-xl mx-auto">
            Check everything you're living with right now. Be honest: nobody sees this but you.
            Most women finish in under two minutes.
          </p>
        </div>

        <div className="bg-card/50 border border-white/10 rounded-[2rem] p-6 md:p-10 backdrop-blur-sm min-h-[520px] relative">
          {/* Progress */}
          <div className="flex items-center justify-between mb-8">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-primary">
              Step {step} of 3
            </span>
            <div className="flex gap-2">
              {[1, 2, 3].map((n) => (
                <span
                  key={n}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    n <= step ? "w-8 bg-primary" : "w-4 bg-white/15"
                  }`}
                />
              ))}
            </div>
          </div>

          {loading ? (
            <div
              className="flex flex-col items-center justify-center py-24 text-center"
              aria-live="polite"
            >
              <Loader2 className="text-primary animate-spin" size={40} />
              <p className="mt-6 font-display font-bold uppercase tracking-tight text-xl text-white">
                {LOAD_TEXT[loadIdx]}
              </p>
            </div>
          ) : (
            <>
              {step === 1 && (
                <Step1
                  data={data}
                  toggleItem={toggleItem}
                />
              )}
              {step === 2 && (
                <Step2 data={data} update={update} checkedOrdered={checkedOrdered} />
              )}
              {step === 3 && <Step3 data={data} update={update} />}

              {error && (
                <p className="mt-5 text-sm font-semibold text-red-400" role="alert">
                  {error}
                </p>
              )}

              {step === 3 && (
                <div className="mt-6 flex justify-center" data-testid="matrix-trust">
                  <TrustpilotBadge />
                </div>
              )}

              <div className="mt-8 flex items-center gap-4">
                {step > 1 && (
                  <button
                    onClick={back}
                    className="inline-flex items-center gap-1 text-white/60 hover:text-white font-semibold text-sm focus-ring rounded-md px-2 py-2"
                  >
                    <ChevronLeft size={18} /> Back
                  </button>
                )}
                <button
                  onClick={step === 3 ? submit : next}
                  className="ml-auto inline-flex items-center rounded-full bg-primary text-primary-foreground font-bold px-8 py-3.5 hover:brightness-110 active:scale-[0.98] transition focus-ring shadow-[0_10px_30px_-8px_rgba(212,175,55,0.6)]"
                >
                  {step === 3 ? "Build My Matrix" : "Continue"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

// ---------- STEP 1 ----------
const Step1 = ({ data, toggleItem }) => (
  <fieldset>
    <legend className="text-lg font-display font-bold uppercase tracking-tight text-white mb-5">
      Check everything you're living with right now.
    </legend>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {SYMPTOMS.map((s) => {
        const checked = data.checkedItems.includes(s.id);
        return (
          <label
            key={s.id}
            className={`flex items-center gap-3 min-h-[44px] px-4 rounded-xl cursor-pointer border transition-colors ${
              checked
                ? "bg-primary/10 border-primary/40"
                : "bg-white/[0.02] border-white/8 hover:border-white/20"
            }`}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => toggleItem(s.id)}
              className="border-white/30 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
            <span className="text-[15px] text-white/85 font-medium leading-snug py-2">
              {s.label}
            </span>
          </label>
        );
      })}
    </div>
  </fieldset>
);

// ---------- STEP 2 ----------
const Step2 = ({ data, update, checkedOrdered }) => {
  const single = checkedOrdered.length === 1;
  return (
    <div className="space-y-9">
      <div>
        <Label className="text-base font-display font-bold uppercase tracking-tight text-white block mb-3">
          From what you identified, which one of these is your biggest problem currently?
        </Label>
        <Select
          value={data.primary_symptom}
          onValueChange={(v) => update({ primary_symptom: v })}
          disabled={single}
        >
          <SelectTrigger className="w-full bg-white/[0.03] border-white/15 text-white h-12 rounded-xl">
            <SelectValue placeholder="Choose the one that hits hardest from your answers…" />
          </SelectTrigger>
          <SelectContent className="bg-popover border-white/15 text-white max-h-72">
            {checkedOrdered.map((s) => (
              <SelectItem key={s.id} value={s.id} className="focus:bg-primary/15">
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-base font-display font-bold uppercase tracking-tight text-white block mb-3">
          Which of the following best describes you right now?
        </Label>
        <RadioGroup value={data.stage} onValueChange={(v) => update({ stage: v })} className="space-y-2">
          {STAGE_OPTIONS.map((o) => (
            <label
              key={o.id}
              className={`flex items-center gap-3 min-h-[44px] px-4 rounded-xl cursor-pointer border transition-colors ${
                data.stage === o.id ? "bg-primary/10 border-primary/40" : "bg-white/[0.02] border-white/8 hover:border-white/20"
              }`}
            >
              <RadioGroupItem value={o.id} className="border-white/30 text-primary" />
              <span className="text-[15px] text-white/85 font-medium">{o.label}</span>
            </label>
          ))}
        </RadioGroup>
      </div>

      <div>
        <Label className="text-base font-display font-bold uppercase tracking-tight text-white block mb-3">
          Are you currently using hormones?
        </Label>
        <RadioGroup value={data.hrt_status} onValueChange={(v) => update({ hrt_status: v })} className="space-y-2">
          {HRT_OPTIONS.map((o) => (
            <label
              key={o.id}
              className={`flex items-center gap-3 min-h-[44px] px-4 rounded-xl cursor-pointer border transition-colors ${
                data.hrt_status === o.id ? "bg-primary/10 border-primary/40" : "bg-white/[0.02] border-white/8 hover:border-white/20"
              }`}
            >
              <RadioGroupItem value={o.id} className="border-white/30 text-primary" />
              <span className="text-[15px] text-white/85 font-medium">{o.label}</span>
            </label>
          ))}
        </RadioGroup>
      </div>
    </div>
  );
};

// ---------- STEP 3 ----------
const Step3 = ({ data, update }) => (
  <div className="space-y-6">
    <div>
      <h3 className="text-lg font-display font-bold uppercase tracking-tight text-white">
        Where should we send your Matrix?
      </h3>
      <p className="mt-2 text-white/65 font-medium">
        Your results appear on the next screen. We'll also email you a copy you can keep and come
        back to.
      </p>
    </div>

    <div className="grid gap-6 sm:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor="first_name" className="text-sm font-semibold text-white/80">First name</Label>
        <Input
          id="first_name"
          value={data.first_name}
          maxLength={40}
          onChange={(e) => update({ first_name: e.target.value })}
          placeholder="Your first name"
          data-testid="matrix-first-name-input"
          className="bg-white/[0.03] border-white/15 text-white h-12 rounded-xl"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="last_name" className="text-sm font-semibold text-white/80">Last name</Label>
        <Input
          id="last_name"
          value={data.last_name}
          maxLength={40}
          onChange={(e) => update({ last_name: e.target.value })}
          placeholder="Your last name"
          data-testid="matrix-last-name-input"
          className="bg-white/[0.03] border-white/15 text-white h-12 rounded-xl"
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label htmlFor="email" className="text-sm font-semibold text-white/80">Email</Label>
      <Input
        id="email"
        type="email"
        value={data.email}
        onChange={(e) => update({ email: e.target.value })}
        placeholder="you@email.com"
        className="bg-white/[0.03] border-white/15 text-white h-12 rounded-xl"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="phone" className="text-sm font-semibold text-white/80">
        Cell phone <span className="text-white/45 font-normal">(optional)</span>
      </Label>
      <Input
        id="phone"
        type="tel"
        value={data.phone}
        onChange={(e) => update({ phone: e.target.value })}
        placeholder="(555) 555-5555"
        className="bg-white/[0.03] border-white/15 text-white h-12 rounded-xl"
      />
      <p className="text-xs text-white/50 leading-relaxed">
        Add your number and we'll text you a link to your results and to book your call.
        Message &amp; data rates may apply; reply STOP to opt out anytime.
      </p>
    </div>

    <label className="flex items-start gap-3 cursor-pointer pt-1">
      <Checkbox
        checked={data.consent_marketing}
        onCheckedChange={(v) => update({ consent_marketing: !!v })}
        className="mt-0.5 border-white/30 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
      />
      <span className="text-sm text-white/70 leading-relaxed">
        I'd like Sassy Full Stack Fitness to email me my results and related information. I can
        unsubscribe anytime.
      </span>
    </label>

    <p className="text-xs text-white/45 leading-relaxed pt-0.5">
      By submitting, you agree to our{" "}
      <a
        href="/privacy"
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary/90 underline underline-offset-2 hover:text-primary transition-colors"
        data-testid="consent-privacy-link"
      >
        Privacy Policy
      </a>{" "}
      and{" "}
      <a
        href="/terms"
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary/90 underline underline-offset-2 hover:text-primary transition-colors"
        data-testid="consent-terms-link"
      >
        Terms
      </a>
      .
    </p>
  </div>
);

export default Matrix;
